/**
 * ServersList — listado de Servidores (estándar de listados del CMDB).
 *
 * Andamiaje común (`common/ListPage.jsx`): buscador, filtros, exportar,
 * columnas, clic en fila → ficha EN LECTURA, botones
 * `[acciones propias] · Copiar · Editar · Eliminar` y diálogos comunes.
 *
 * Lo propio de esta pantalla:
 *  · Vistas: **Lista** (por defecto), **Fichas** y **Kanban** (por entorno).
 *  · Filtros: Entorno, Estado, Licencia, Ubicación, Rack, Rol, 📡 Agente,
 *    Hypervisor, Cluster y «es nodo de chasis», más los interruptores
 *    «solo con avisos de almacenamiento» y «ver fusionados».
 *  · Acciones propias: **Ver rack** (abre el rack con el servidor resaltado),
 *    **Componentes** (NICs/HBAs), **Discos**, **Ver chasis** (si es nodo de un
 *    chasis multinodo) y **Sincronizar con el agente** (si tiene agente).
 *
 * Se conservan los comportamientos que ya existían y no se ven en el listado:
 *  · el estado de alimentación por rack (⚡ redundante / fuente única / ninguna);
 *  · los avisos de almacenamiento y de configuración pendiente;
 *  · el resaltado y centrado con `?destacar=<id>` (convención de los módulos);
 *  · la actualización en vivo por WebSocket y el refresco cada 30 s;
 *  · la navegación anterior/siguiente dentro de la ficha.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CubeIcon,
  CpuChipIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ViewColumnsIcon,
  CircleStackIcon,
  ServerStackIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useSocket } from '../contexts/SocketContext';
import useAgentPresence from '../hooks/useAgentPresence';
import ServerForm from './ServerForm';
import { getServerFormStyle } from '../utils/serverFormStyle';
import ComponentsModal from './ComponentsModal';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import { getServerSetupWarnings, normalizeSetupOmitted } from '../utils/serverSetupWarnings';
import { tagList } from '../utils/tags';
import useLocationPaths from '../hooks/useLocationPaths';

/* ── Etiquetas y colores (clases del sistema de diseño, no colores en duro) ── */
const STATUS_BADGE = {
  active: 'list-badge--on',
  maintenance: 'list-badge--warn',
  decom: 'list-badge--off',
  merged: 'list-badge--off',
};
const STATUS_LABEL = {
  active: 'Activo',
  maintenance: 'Mantenimiento',
  decom: 'Decom',
  merged: 'Fusionado',
};
const ENV_META = {
  prod: { label: 'Producción', icon: '🟢' },
  pre: { label: 'Pre-producción', icon: '🟡' },
  dev: { label: 'Desarrollo', icon: '🔵' },
};
const ENV_ORDER = ['prod', 'pre', 'dev', ''];

const envMeta = (env) => ENV_META[env] || { label: env || 'Sin entorno', icon: '⚪' };

function buildStorageAdvisoryMap(payload) {
  const items = Array.isArray(payload?.servers) ? payload.servers : [];
  return items.reduce((acc, item) => {
    const serverId = item?.server_id;
    if (!serverId) return acc;
    const current = acc[serverId] || { count: 0, severity: 'warning', titles: [] };
    current.count += 1;
    if (item?.severity === 'danger') current.severity = 'danger';
    if (item?.title) current.titles.push(item.title);
    acc[serverId] = current;
    return acc;
  }, {});
}

function _timeAgo(date) {
  const s = Math.round((Date.now() - date.getTime()) / 1000);
  if (s < 60) return 'hace unos segundos';
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
}

function _parseBackendDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildServerRoleDisplay(server) {
  const labels = [];
  const seen = new Set();
  const addLabel = (value) => {
    const label = String(value || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  };
  const manualRoles = Array.isArray(server?.manual_roles)
    ? server.manual_roles
    : (Array.isArray(server?.roles) ? server.roles : []);
  manualRoles.forEach((role) => addLabel(role?.code || role?.name));
  (Array.isArray(server?.detected_roles) ? server.detected_roles : []).forEach(addLabel);
  return labels.join(', ');
}

function ServersList({ companyId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const socket = useSocket();
  // Presencia en vivo: manda sobre el `agent_status` guardado, que puede ir
  // hasta 5 minutos por detrás. Así el listado recién cargado tampoco miente.
  const presence = useAgentPresence(companyId);
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  const [servers, setServers] = useState([]);
  const { pathFor } = useLocationPaths(companyId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [selectedServer, setSelectedServer] = useState(null);
  // Estilo de ficha (clásica o tarjetas beta). Se lee una vez al montar: el
  // toggle de la propia ficha recarga la página, y Settings se abre en otra ruta.
  const [estiloFicha] = useState(getServerFormStyle);
  const [componentsServer, setComponentsServer] = useState(null);
  const [disksServer, setDisksServer] = useState(null);
  const [disks, setDisks] = useState(null);
  const [serverToCopy, setServerToCopy] = useState(null);
  const [cloningId, setCloningId] = useState(null);
  const [syncServer, setSyncServer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const [search, setSearch] = useState('');
  const [filterEnv, setFilterEnv] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLicense, setFilterLicense] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterRack, setFilterRack] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterHypervisor, setFilterHypervisor] = useState('');
  const [filterCluster, setFilterCluster] = useState('');
  const [filterChassisNode, setFilterChassisNode] = useState('');
  const [advisoriesOnly, setAdvisoriesOnly] = useState('');
  const [showMerged, setShowMerged] = useState('');

  const [powerStatus, setPowerStatus] = useState({});
  const [storageAdvisories, setStorageAdvisories] = useState({});
  const [hlId, setHlId] = useState(null);

  /* ── Carga de datos ─────────────────────────────────────────────────── */
  const fetchPowerStatus = useCallback(async (serverList) => {
    if (!companyId || !serverList.length) return;
    const rackIds = [...new Set(serverList.map((s) => s.rack_id).filter(Boolean))];
    if (!rackIds.length) return;
    try {
      const results = await Promise.all(
        rackIds.map((rid) => api.getRackPowerSummary(rid, companyId).catch(() => null)),
      );
      const map = {};
      results.forEach((res) => {
        (res?.devices || []).forEach((d) => {
          if (d.consumer_type !== 'rack_element') return;
          const pduCount = d.pdu_count ?? 0;
          map[d.consumer_id] = pduCount >= 2 ? 'redundant' : pduCount === 1 ? 'single' : 'none';
        });
      });
      setPowerStatus(map);
    } catch { /* silencioso: es información de apoyo */ }
  }, [companyId]);

  const fetchServers = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError('');
      if (!companyId) {
        setError(tr('msg.select_company', 'Selecciona una empresa para ver servidores.'));
        setServers([]);
        setStorageAdvisories({});
        return [];
      }
      const [response, advisoryResponse] = await Promise.all([
        api.getServers(companyId),
        api.getStorageAdvisories(companyId).catch(() => ({ servers: [] })),
      ]);
      const normalized = (response.data || []).map((srv) => {
        const roleDisplay = buildServerRoleDisplay(srv);
        return {
          ...srv,
          setup_omitted: normalizeSetupOmitted(srv.setup_omitted),
          role: srv.role || roleDisplay,
          role_display: roleDisplay,
        };
      });
      setServers(normalized);
      setStorageAdvisories(buildStorageAdvisoryMap(advisoryResponse));
      fetchPowerStatus(normalized);
      return normalized;
    } catch (err) {
      setError(err.message);
      setServers([]);
      setStorageAdvisories({});
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [companyId, fetchPowerStatus, tr]);

  useEffect(() => { fetchServers(); }, [fetchServers]);

  // ── Actualización en vivo ──
  useEffect(() => {
    if (!socket) return undefined;
    const onConnect = () => fetchServers({ silent: true });
    const onCreated = () => fetchServers({ silent: true });
    const onUpdated = (srv) => {
      setServers((prev) => prev.map((s) => {
        if (s.id !== srv.id) return s;
        const roleDisplay = buildServerRoleDisplay(srv);
        return { ...s, ...srv, role: srv.role || roleDisplay, role_display: roleDisplay };
      }));
    };
    const onDeleted = ({ id }) => setServers((prev) => prev.filter((s) => s.id !== id));
    // Presencia en vivo: el socket del agente manda sobre el `agent_status`
    // guardado, que puede ir hasta 5 minutos por detrás.
    const onAgentPresence = ({ agent_id, online, company_id }) => {
      if (!agent_id) return;
      if (company_id && companyId && Number(company_id) !== Number(companyId)) return;
      setServers((prev) => prev.map((s) => (
        String(s.agent_id || '') === String(agent_id)
          ? { ...s, agent_status: online ? 'online' : 'offline', last_seen_at: new Date().toISOString() }
          : s
      )));
    };
    socket.on('connect', onConnect);
    socket.on('server:created', onCreated);
    socket.on('server:updated', onUpdated);
    socket.on('server:deleted', onDeleted);
    socket.on('agent:presence', onAgentPresence);
    return () => {
      socket.off('connect', onConnect);
      socket.off('server:created', onCreated);
      socket.off('server:updated', onUpdated);
      socket.off('server:deleted', onDeleted);
      socket.off('agent:presence', onAgentPresence);
    };
  }, [socket, fetchServers, companyId]);

  // ── Refresco periódico ──
  useEffect(() => {
    if (!companyId) return undefined;
    const timerId = window.setInterval(() => { fetchServers({ silent: true }); }, 30000);
    return () => window.clearInterval(timerId);
  }, [companyId, fetchServers]);

  /* ── Apertura de la ficha (lectura por defecto) ─────────────────────── */
  const handleOpen = (server) => { setSelectedServer(server); setFormMode('view'); setShowForm(true); };
  const handleEdit = (server) => { setSelectedServer(server); setFormMode('edit'); setShowForm(true); };
  const handleCreate = () => { setSelectedServer(null); setFormMode('edit'); setShowForm(true); };
  useAutoOpen(servers, handleOpen);

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedServer(null);
    fetchServers();
  };

  // Refresca el listado pero mantiene la ficha abierta (no se toca
  // `selectedServer`: eso reiniciaría campos recién guardados como position_u).
  // Solo al CREAR se adopta el registro nuevo, para que la ficha siga abierta
  // con su id en vez de cerrarse.
  const handleFormSave = async (creado) => {
    await fetchServers();
    if (creado?.id) { setSelectedServer(creado); setFormMode('edit'); }
  };

  const handleOpenServer = async (serverId) => {
    const id = Number(serverId);
    if (!id) return;
    const refreshed = await fetchServers({ silent: true });
    const source = Array.isArray(refreshed) && refreshed.length ? refreshed : servers;
    const target = source.find((srv) => Number(srv.id) === id);
    if (!target) throw new Error('No se ha encontrado el CI destino tras la fusión');
    setSelectedServer(target);
    setShowForm(true);
  };

  // ── Resaltado y centrado con ?destacar=<id> (convención de los módulos) ──
  //    Con `&abrir=1` se abre además la ficha (enlaces ficha → ficha, como el
  //    que llega desde la ficha de un chasis multinodo).
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !servers.length) return undefined;
    const id = Number(raw);
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) {
      const target = servers.find((s) => s.id === id);
      if (target) { handleOpen(target); return undefined; }
    }
    setHlId(id);
    const row = document.getElementById(`server-row-${id}`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setHlId(null), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, servers, setSearchParams]);

  /* ── Borrado (elimina también componentes, discos y conexiones) ─────── */
  const handleDelete = async (server) => {
    try {
      const response = await api.deleteServer(server.id, companyId);
      if (!response?.success) throw new Error(response?.message || 'No se pudo eliminar');
      await fetchServers();
    } catch (err) {
      setError(err.message || tr('server.delete_error', 'No se pudo eliminar el servidor'));
    }
  };

  /* ── Discos ─────────────────────────────────────────────────────────── */
  const openDisks = async (server) => {
    setDisksServer(server);
    setDisks(null);
    setDialogError('');
    try {
      const resp = await api.getServerStorage(server.id, companyId);
      const data = resp?.data?.data && typeof resp.data.data === 'object' ? resp.data.data : resp?.data;
      setDisks(Array.isArray(data?.physical_disks) ? data.physical_disks : []);
    } catch (err) {
      setDialogError(err.message || tr('server.disks_error', 'No se pudieron cargar los discos'));
      setDisks([]);
    }
  };

  /* ── Copiar ─────────────────────────────────────────────────────────── */
  const buildClonePayload = (srv, nameOverride = null) => {
    const tags = Array.isArray(srv.tags)
      ? srv.tags
      : typeof srv.tags === 'string'
        ? srv.tags.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
    return {
      name: nameOverride || srv.name || '',
      manufacturer: srv.manufacturer || '',
      model: srv.model || '',
      serial_number: '',
      asset_tag: '',
      hostname: srv.hostname || '',
      ip_address: '',
      os: srv.os || '',
      environment: srv.environment || 'prod',
      status: srv.status || 'active',
      location_id: srv.location_id || null,
      location_name: srv.location_name || '',
      rack_id: null,
      rack_name: '',
      position_u: null,
      form_factor: srv.form_factor || 'rack',
      size_u: srv.size_u || 1,
      owner: srv.owner || '',
      license: srv.license || '',
      license_status: srv.license_status || 'vigente',
      license_expires_at: srv.license_expires_at || '',
      cpu: srv.cpu || '',
      ram_gb: srv.ram_gb || '',
      storage_tb: srv.storage_tb || '',
      hypervisor: srv.hypervisor || '',
      cluster: srv.cluster || '',
      tags,
      chassis_id: null,
      chassis_node: null,
    };
  };

  /**
   * Copia un servidor: configuración (hardware, roles, discos y componentes) sí;
   * identidad física no (serie, asset, IP, MAC/WWN y posición en rack).
   */
  const handleCopy = async (name) => {
    const srv = serverToCopy;
    if (!srv?.id) return;
    setCloningId(srv.id);
    setDialogError('');
    try {
      const [storageRes, compRes] = await Promise.all([
        api.getServerStorage(srv.id, companyId).catch(() => null),
        api.getComponents(srv.id, companyId).catch(() => null),
      ]);

      let suggestedName = null;
      try {
        const sugg = await api.suggestName({
          type: 'server',
          companyId,
          locationId: srv.location_id || undefined,
          rackId: srv.rack_id || undefined,
        });
        suggestedName = sugg?.data?.name || sugg?.name || null;
      } catch { /* sin sugerencia: se usa el nombre pedido */ }

      const payload = buildClonePayload(srv, name || suggestedName || 'PENDIENTE-NOMBRE');
      const createResp = await api.createServer(payload, companyId);
      const newServerId = createResp?.data?.id || createResp?.id || createResp?.server?.id;
      if (!newServerId) throw new Error('No se pudo obtener el ID del servidor clonado');

      const components = Array.isArray(compRes?.data) ? compRes.data : [];
      const portIdMap = new Map();
      for (const comp of components) {
        const { ports = [], id, element_id, created_at, updated_at, ...rest } = comp;
        const compResp = await api.createComponent(
          newServerId, { ...rest, serial_number: '', asset_tag: '' }, companyId,
        );
        const newCompId = compResp?.data?.id || compResp?.id || compResp?.component?.id;
        if (!newCompId) continue;
        for (const port of ports) {
          const { id: oldPortId, created_at: ca, updated_at: ua, connection_id, ...pRest } = port;
          const portResp = await api.createComponentPort(
            newServerId, newCompId, { ...pRest, mac_address: '', wwn: '' }, companyId,
          );
          const newPortId = portResp?.data?.id || portResp?.id || portResp?.port?.id;
          if (oldPortId && newPortId) portIdMap.set(Number(oldPortId), Number(newPortId));
        }
      }

      if (storageRes?.data) {
        const meta = {
          inventory_date: storageRes.data.inventory_date || '',
          taken_by: storageRes.data.taken_by || '',
        };
        const data = storageRes.data.data && typeof storageRes.data.data === 'object'
          ? JSON.parse(JSON.stringify(storageRes.data.data))
          : {};
        if (Array.isArray(data.physical_disks)) {
          // El slot SÍ se copia (es un identificador por servidor); el número de
          // serie no, que es la identidad del disco.
          data.physical_disks = data.physical_disks.map((d) => ({ ...d, serial: '' }));
        }
        if (Array.isArray(data.network_teams)) {
          data.network_teams = data.network_teams.map((team) => ({
            ...team,
            member_port_ids: (team.member_port_ids || [])
              .map((pid) => portIdMap.get(Number(pid)))
              .filter(Boolean),
          }));
        }
        await api.updateServerStorage(newServerId, {
          inventory_date: meta.inventory_date, taken_by: meta.taken_by, data,
        }, companyId);
      }

      setServerToCopy(null);
      await fetchServers();
    } catch (err) {
      setDialogError(err.message || tr('server.copy_error', 'Error al copiar servidor'));
    } finally {
      setCloningId(null);
    }
  };

  /* ── Sincronizar con el agente ──────────────────────────────────────── */
  const handleSyncAgent = async () => {
    const srv = syncServer;
    if (!srv?.agent_id) return;
    setBusy(true);
    setDialogError('');
    try {
      await api.enqueueAgentCommand(companyId, {
        agent_id: srv.agent_id,
        command: 'rescan',
        ci_type: 'rack_element',
        ci_id: srv.id,
        payload: { source: 'servers_list', requested_from: 'list' },
      });
      setSyncServer(null);
    } catch (err) {
      setDialogError(err.message || tr('server.sync_error', 'No se pudo enviar el comando al agente'));
    } finally {
      setBusy(false);
    }
  };

  /* ── Datos derivados y opciones de filtro ───────────────────────────── */
  const optionsFrom = (values) => [...new Set(values.filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
    .map((v) => ({ value: String(v), label: String(v) }));

  const envOptions = useMemo(() => optionsFrom(servers.map((s) => s.environment)), [servers]);
  const roleOptions = useMemo(
    () => optionsFrom(servers.flatMap((s) => String(s.role_display || s.role || '')
      .split(',').map((r) => r.trim()))),
    [servers],
  );
  // El filtro sigue trabajando con el nombre (es lo que trae el listado), pero
  // la etiqueta muestra la ruta completa para no confundir «Sala 1» de sedes distintas.
  const locationOptions = useMemo(
    () => optionsFrom(servers.map((s) => s.location_name)).map((opt) => {
      const hit = servers.find((s) => s.location_name === opt.value);
      return { value: opt.value, label: (hit && pathFor(hit.location_id)) || opt.label };
    }),
    [servers, pathFor],
  );
  const rackOptions = useMemo(() => optionsFrom(servers.map((s) => s.rack_name)), [servers]);
  const hypervisorOptions = useMemo(
    () => optionsFrom(servers.map((s) => s.hypervisor)), [servers],
  );
  const clusterOptions = useMemo(() => optionsFrom(servers.map((s) => s.cluster)), [servers]);

  const mergedServersCount = useMemo(
    () => servers.filter((srv) => (srv.status || '').toLowerCase() === 'merged').length,
    [servers],
  );

  const filteredServers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return servers.filter((srv) => {
      const isMerged = (srv.status || '').toLowerCase() === 'merged';
      if (term) {
        const haystack = [
          srv.name, srv.hostname, srv.ip_address, srv.role, srv.role_display, srv.owner, srv.os,
          srv.location_name, srv.rack_name, srv.chassis_name, tagList(srv.tags).join(','),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (filterEnv && (srv.environment || '') !== filterEnv) return false;
      if (filterStatus && (srv.status || '') !== filterStatus) return false;
      if (filterLicense && (srv.license_status || '') !== filterLicense) return false;
      if (filterLocation && (srv.location_name || '') !== filterLocation) return false;
      if (filterRack && (srv.rack_name || '') !== filterRack) return false;
      if (filterRole && !String(srv.role_display || srv.role || '').toLowerCase().includes(filterRole.toLowerCase())) return false;
      if (filterAgent) {
        const agent = srv.agent_status || 'none';
        if (filterAgent === 'none' && srv.agent_id) return false;
        if (filterAgent === 'online' && agent !== 'online') return false;
        if (filterAgent === 'offline' && agent !== 'offline') return false;
      }
      if (filterHypervisor && (srv.hypervisor || '') !== filterHypervisor) return false;
      if (filterCluster && (srv.cluster || '') !== filterCluster) return false;
      if (filterChassisNode === 'yes' && srv.chassis_id == null) return false;
      if (filterChassisNode === 'no' && srv.chassis_id != null) return false;
      if (advisoriesOnly && !storageAdvisories[srv.id]) return false;
      if (!showMerged && isMerged) return false;
      return true;
    });
  }, [servers, search, filterEnv, filterStatus, filterLicense, filterLocation, filterRack,
    filterRole, filterAgent, filterHypervisor, filterCluster, filterChassisNode,
    advisoriesOnly, showMerged, storageAdvisories]);

  // Orden de las fichas congelado mientras la ficha está abierta (ver hook).
  const navServers = useNavOrder(filteredServers, selectedServer, showForm);

  /* ── Celdas (mismo significado que la tabla de siempre) ─────────────── */
  const cellText = (key, server) => {
    if (key === 'agent_status') {
      if (server.agent_status === 'online') return 'Online';
      if (server.agent_status === 'offline') return 'Offline';
      return 'Sin agente';
    }
    if (key === 'status') return STATUS_LABEL[(server.status || '').toLowerCase()] || server.status || '';
    if (key === 'license_status') {
      if (server.license_status === 'vigente') return 'Vigente';
      if (server.license_status === 'expirada') return 'Expirada';
      return server.license_status || '';
    }
    if (key === 'hypervisor') return server.hypervisor || (server.is_hypervisor ? 'Host hipervisor' : '');
    if (key === 'power_status') {
      const ps = powerStatus[server.id];
      if (ps === 'redundant') return 'Redundante';
      if (ps === 'single') return 'Fuente única';
      if (ps === 'none') return 'Sin alimentación';
      return '';
    }
    if (key === 'license_expires_at' || key === 'last_check_at') {
      return server[key] ? new Date(server[key]).toLocaleDateString() : '';
    }
    if (key === 'tags') return tagList(server.tags).join(', ');
    if (key === 'role') return server.role_display || server.role || '';
    if (key === 'location_name') return pathFor(server.location_id) || server.location_name || '';
    const value = server[key];
    return value === undefined || value === null ? '' : value;
  };

  const renderCell = (key, server) => {
    const storageReview = storageAdvisories[server.id];
    const setupWarnings = getServerSetupWarnings(server);

    if (key === 'name') {
      const isNew = server.created_at && (Date.now() - new Date(server.created_at).getTime()) < 7 * 86400000;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>{server.name}</span>
          {isNew && (
            <span
              className="list-badge list-badge--on"
              title={`${tr('server.new_ci', 'Alta')}: ${new Date(server.created_at).toLocaleDateString()}`}
            >
              ✨ {tr('server.badge_new', 'Nuevo')}
            </span>
          )}
          {storageReview && (
            <span
              className="list-badge list-badge--warn"
              title={storageReview.titles.join(' · ') || tr('server.storage_review', 'Almacenamiento pendiente de revisión')}
            >
              {storageReview.count}
            </span>
          )}
          {setupWarnings.length > 0 && (
            <span
              className="list-badge list-badge--warn"
              title={setupWarnings.map((w) => w.title).join(' · ')}
            >
              !{setupWarnings.length}
            </span>
          )}
        </span>
      );
    }

    if (key === 'agent_status') {
      const livePresence = server.agent_id ? presence[server.agent_id] : null;
      const agentStatus = livePresence
        ? (livePresence.online ? 'online' : 'offline')
        : server.agent_status;
      const parsedLastSeen = _parseBackendDate(server.last_seen_at);
      const ago = livePresence
        ? tr('server.agent_live', 'en vivo')
        : (parsedLastSeen ? _timeAgo(parsedLastSeen) : null);
      if (agentStatus === 'online') {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span className="list-badge list-badge--on">● Online</span>
            {ago && <span className="list-hint">{ago}</span>}
          </span>
        );
      }
      if (agentStatus === 'offline') {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span className="list-badge list-badge--off">● Offline</span>
            {ago && <span className="list-hint">{ago}</span>}
          </span>
        );
      }
      return <span className="list-hint">—</span>;
    }

    if (key === 'status') {
      const val = (server.status || '').toLowerCase();
      return (
        <span className={`list-badge ${STATUS_BADGE[val] || 'list-badge--off'}`}>
          {STATUS_LABEL[val] || server.status || '—'}
        </span>
      );
    }

    if (key === 'environment') {
      const meta = envMeta(server.environment);
      return <span>{meta.icon} {meta.label}</span>;
    }

    if (key === 'license_status') {
      const val = (server.license_status || '').toLowerCase();
      if (!val) return <span className="list-hint">—</span>;
      const expired = val === 'expirada';
      return (
        <span className={`list-badge ${expired ? 'list-badge--off' : 'list-badge--on'}`}>
          {expired ? tr('server.license_expired', 'Expirada') : tr('server.license_ok', 'Vigente')}
        </span>
      );
    }

    if (key === 'license_expires_at' || key === 'last_check_at') {
      return server[key] ? new Date(server[key]).toLocaleDateString() : <span className="list-hint">—</span>;
    }

    if (key === 'tags') {
      const tags = Array.isArray(server.tags) ? server.tags : String(server.tags || '').split(',').filter(Boolean);
      return tags.length ? tags.join(', ') : <span className="list-hint">—</span>;
    }

    if (key === 'ram_gb') return server.ram_gb !== undefined ? `${server.ram_gb} GB` : <span className="list-hint">—</span>;
    if (key === 'storage_tb') return server.storage_tb !== undefined ? `${server.storage_tb} TB` : <span className="list-hint">—</span>;
    if (key === 'ip_address') return server.ip_address ? <span className="list-code">{server.ip_address}</span> : <span className="list-hint">—</span>;
    if (key === 'role') return server.role_display || server.role || <span className="list-hint">—</span>;
    if (key === 'chassis_name') {
      if (!server.chassis_id) return <span className="list-hint">—</span>;
      return (
        <button
          type="button"
          className="list-btn-secondary"
          style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
          onClick={(event) => { event.stopPropagation(); navigate(`/infrastructure/chassis?destacar=${server.chassis_id}&abrir=1`); }}
          title={tr('server.open_chassis', 'Abrir la ficha del chasis')}
        >
          {server.chassis_name || `#${server.chassis_id}`}
          {server.chassis_node != null && ` · N${server.chassis_node}`}
        </button>
      );
    }
    if (key === 'chassis_node') {
      return server.chassis_node != null
        ? <span className="list-code">N{server.chassis_node}</span>
        : <span className="list-hint">—</span>;
    }

    if (key === 'hypervisor') {
      const associatedHostName = String(server.hypervisor || '').trim();
      const typeRaw = String(server.detected_hypervisor_type || '').trim().toLowerCase();
      const typeLabel = typeRaw === 'hyper-v' || typeRaw === 'hyper_v'
        ? 'Hyper-V'
        : typeRaw === 'vmware' || typeRaw === 'vmware_esxi' ? 'VMware'
          : typeRaw === 'kvm' ? 'KVM'
            : typeRaw === 'proxmox' ? 'Proxmox' : (typeRaw || null);
      const isHypervisor = Boolean(server.is_hypervisor);
      if (!associatedHostName && !isHypervisor && !typeLabel) return <span className="list-hint">—</span>;
      const hostTarget = associatedHostName
        ? servers.find((item) => {
          const needle = associatedHostName.toLowerCase();
          return String(item.name || '').trim().toLowerCase() === needle
            || String(item.hostname || '').trim().toLowerCase() === needle;
        })
        : null;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {associatedHostName ? (
            hostTarget ? (
              <button
                type="button"
                className="list-btn-secondary"
                style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                onClick={(event) => { event.stopPropagation(); handleOpen(hostTarget); }}
                title={tr('server.open_hypervisor', 'Abrir el servidor hypervisor asociado')}
              >
                {associatedHostName}
              </button>
            ) : <span>{associatedHostName}</span>
          ) : <span>{tr('server.hypervisor_host', 'Host hipervisor')}</span>}
          {(typeLabel || isHypervisor) && (
            <span className="list-badge list-badge--on">{typeLabel || 'Hypervisor'}</span>
          )}
        </span>
      );
    }

    if (key === 'power_status') {
      const ps = powerStatus[server.id];
      if (ps === 'redundant') return <span className="list-badge list-badge--on">✅ {tr('server.power_redundant', 'Redundante')}</span>;
      if (ps === 'single') return <span className="list-badge list-badge--warn">⚠️ {tr('server.power_single', 'Fuente única')}</span>;
      if (ps === 'none') return <span className="list-badge list-badge--off">❌ {tr('server.power_none', 'Sin alimentación')}</span>;
      return <span className="list-hint">—</span>;
    }

    const value = server[key];
    return value === undefined || value === null || value === '' ? <span className="list-hint">—</span> : value;
  };

  /* ── Columnas (catálogo completo; 14 visibles de entrada) ───────────── */
  /**
   * Cada columna pinta su celda con `renderCell`, que es quien da formato
   * (píldoras de estado, avisos, enlaces al chasis/hypervisor…) y el guion de
   * «sin valor». El catálogo solo declara clave, etiqueta y orden.
   */
  const allColumns = useMemo(() => {
    const catalog = [
      { key: 'name', label: tr('server.name', 'Nombre'), sortable: true, sortValue: (s) => s.name },
    { key: 'hostname', label: tr('server.hostname', 'Hostname'), sortable: true },
    { key: 'ip_address', label: tr('server.ip', 'IP'), sortable: true },
    { key: 'os', label: tr('server.os', 'Sistema operativo'), sortable: true },
    { key: 'environment', label: tr('server.environment', 'Entorno'), sortable: true, sortValue: (s) => s.environment || '' },
    { key: 'status', label: tr('server.status', 'Estado'), sortable: true, sortValue: (s) => s.status || '' },
    { key: 'agent_status', label: '📡 ' + tr('server.agent', 'Agente'), sortable: true, sortValue: (s) => s.agent_status || '' },
    { key: 'location_name', label: tr('server.location', 'Ubicación'), sortable: true, sortValue: (s) => pathFor(s.location_id) || s.location_name || '', render: (s) => pathFor(s.location_id) || s.location_name || <span className="list-hint">—</span> },
    { key: 'rack_name', label: tr('server.rack', 'Rack'), sortable: true, sortValue: (s) => s.rack_name || '' },
    { key: 'chassis_name', label: tr('server.chassis', 'Chasis'), sortable: true, sortValue: (s) => s.chassis_name || '' },
    { key: 'chassis_node', label: tr('server.chassis_node', 'Nodo'), sortable: true, sortValue: (s) => s.chassis_node || 0 },
    { key: 'role', label: tr('server.role', 'Rol'), sortable: true, sortValue: (s) => s.role_display || s.role || '' },
    { key: 'owner', label: tr('server.owner', 'Equipo'), sortable: true },
    { key: 'license', label: tr('server.license', 'Licencia SO'), sortable: true },
    { key: 'license_status', label: tr('server.license_status', 'Estado licencia'), sortable: true, sortValue: (s) => s.license_status || '' },
    { key: 'license_expires_at', label: tr('server.license_expires_at', 'Vencimiento'), sortable: true, sortValue: (s) => s.license_expires_at || '' },
    { key: 'last_check_at', label: tr('server.last_check', 'Último check'), sortable: true, sortValue: (s) => s.last_check_at || '' },
    { key: 'cpu', label: 'CPU', sortable: true },
    { key: 'ram_gb', label: 'RAM (GB)', sortable: true, sortValue: (s) => s.ram_gb || 0 },
    { key: 'storage_tb', label: tr('server.storage_tb', 'Almacenamiento (TB)'), sortable: true, sortValue: (s) => s.storage_tb || 0 },
    { key: 'hypervisor', label: tr('server.hypervisor', 'Hypervisor'), sortable: true, sortValue: (s) => s.hypervisor || '' },
    { key: 'cluster', label: tr('server.cluster', 'Cluster'), sortable: true },
    { key: 'tags', label: tr('server.tags', 'Tags'), sortable: true, sortValue: (s) => tagList(s.tags).join(',') },
    { key: 'power_status', label: '⚡ ' + tr('server.power', 'Alimentación'), sortable: true, sortValue: (s) => powerStatus[s.id] || '' },
    ];
    return catalog.map((col) => ({ ...col, render: (row) => renderCell(col.key, row) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tr, powerStatus, storageAdvisories, servers, pathFor]);

  const defaultVisible = [
    'name', 'hostname', 'ip_address', 'os', 'environment', 'status', 'agent_status',
    'location_name', 'rack_name', 'chassis_name', 'role', 'hypervisor', 'owner', 'power_status',
  ];

  const exportRows = useMemo(
    () => filteredServers.map((server) => {
      const row = {};
      allColumns.forEach((col) => { row[col.label] = cellText(col.key, server); });
      return row;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredServers, powerStatus, allColumns],
  );

  /* ── Filtros ────────────────────────────────────────────────────────── */
  const filters = [
    {
      key: 'env', label: tr('server.environment', 'Entorno'),
      allLabel: tr('server.filter_env_all', 'Todos los entornos'),
      value: filterEnv, onChange: setFilterEnv, options: envOptions,
    },
    {
      key: 'status', label: tr('server.status', 'Estado'),
      allLabel: tr('server.filter_status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('status.active', 'Activo') },
        { value: 'maintenance', label: tr('status.maintenance', 'Mantenimiento') },
        { value: 'decom', label: 'Decom' },
        ...(mergedServersCount ? [{ value: 'merged', label: STATUS_LABEL.merged }] : []),
      ],
    },
    {
      key: 'license', label: tr('server.license', 'Licencia'),
      allLabel: tr('server.filter_license_all', 'Todas las licencias'),
      value: filterLicense, onChange: setFilterLicense,
      options: [
        { value: 'vigente', label: tr('server.license_ok', 'Vigente') },
        { value: 'expirada', label: tr('server.license_expired', 'Expirada') },
      ],
    },
    {
      key: 'location', label: tr('server.location', 'Ubicación'),
      allLabel: tr('server.filter_location_all', 'Todas las ubicaciones'),
      value: filterLocation, onChange: setFilterLocation, options: locationOptions,
    },
    {
      key: 'rack', label: tr('server.rack', 'Rack'),
      allLabel: tr('server.filter_rack_all', 'Todos los racks'),
      value: filterRack, onChange: setFilterRack, options: rackOptions,
    },
    {
      key: 'role', label: tr('server.role', 'Rol'),
      allLabel: tr('server.filter_role_all', 'Todos los roles'),
      value: filterRole, onChange: setFilterRole, options: roleOptions,
    },
    {
      key: 'agent', label: '📡 ' + tr('server.agent', 'Agente'),
      allLabel: tr('server.filter_agent_all', 'Cualquier agente'),
      value: filterAgent, onChange: setFilterAgent,
      options: [
        { value: 'online', label: tr('server.agent_online', 'Con agente online') },
        { value: 'offline', label: tr('server.agent_offline', 'Agente caído') },
        { value: 'none', label: tr('server.agent_none', 'Sin agente') },
      ],
    },
    {
      key: 'hypervisor', label: tr('server.hypervisor', 'Hypervisor'),
      allLabel: tr('server.filter_hypervisor_all', 'Cualquier hypervisor'),
      value: filterHypervisor, onChange: setFilterHypervisor, options: hypervisorOptions,
    },
    {
      key: 'cluster', label: tr('server.cluster', 'Cluster'),
      allLabel: tr('server.filter_cluster_all', 'Cualquier cluster'),
      value: filterCluster, onChange: setFilterCluster, options: clusterOptions,
    },
    {
      key: 'chassis', label: tr('server.chassis', 'Chasis'),
      allLabel: tr('server.filter_chassis_all', 'Con y sin chasis'),
      value: filterChassisNode, onChange: setFilterChassisNode,
      options: [
        { value: 'yes', label: tr('server.filter_chassis_nodes', 'Nodos de chasis') },
        { value: 'no', label: tr('server.filter_chassis_standalone', 'Servidores sueltos') },
      ],
    },
    {
      key: 'advisories', type: 'toggle',
      label: '⚠️ ' + tr('server.filter_advisories', 'Con avisos'),
      title: tr('server.filter_advisories_hint', 'Solo servidores con avisos de almacenamiento'),
      value: advisoriesOnly, onValue: 'yes', onChange: setAdvisoriesOnly,
    },
    {
      key: 'merged', type: 'toggle',
      label: tr('server.filter_merged', 'Ver fusionados'),
      title: tr('server.filter_merged_hint', 'Mostrar los CI fusionados, que se ocultan por defecto'),
      value: showMerged, onValue: 'yes', onChange: setShowMerged,
    },
  ];

  /* ── Vistas ─────────────────────────────────────────────────────────── */
  const SERVER_VIEWS = useMemo(() => [
    { key: 'list', label: tr('server.view_list', 'Lista'), icon: ListBulletIcon, title: tr('server.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('server.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('server.view_cards_hint', 'Tarjetas con lo esencial de cada servidor'), render: renderServerCards },
    { key: 'kanban', label: tr('server.view_kanban', 'Kanban'), icon: ViewColumnsIcon, title: tr('server.view_kanban_hint', 'Tablero por entorno: producción, pre-producción y desarrollo'), render: renderServerKanban },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, storageAdvisories, powerStatus]);
  useListView('servers', 'list');

  const subtitle = `${servers.length} ${tr('server.plural', 'servidores')}`
    + ` · ${filteredServers.length} ${tr('location.filtered', 'visibles')}`
    + (mergedServersCount && !showMerged ? ` · ${mergedServersCount} ${tr('server.merged_hidden', 'fusionados ocultos')}` : '');

  /** Aviso compacto reutilizado por las tarjetas y el kanban. */
  function renderServerFlags(server) {
    const storageReview = storageAdvisories[server.id];
    const setupWarnings = getServerSetupWarnings(server);
    const ps = powerStatus[server.id];
    if (!storageReview && !setupWarnings.length && !ps) return null;
    return (
      <div className="list-card-meta">
        {storageReview && (
          <span className="list-badge list-badge--warn" title={storageReview.titles.join(' · ')}>
            ⚠ {storageReview.count}
          </span>
        )}
        {setupWarnings.length > 0 && (
          <span className="list-badge list-badge--warn" title={setupWarnings.map((w) => w.title).join(' · ')}>
            !{setupWarnings.length}
          </span>
        )}
        {ps === 'redundant' && <span className="list-badge list-badge--on">✅ {tr('server.power_redundant', 'Redundante')}</span>}
        {ps === 'single' && <span className="list-badge list-badge--warn">⚠️ {tr('server.power_single', 'Fuente única')}</span>}
        {ps === 'none' && <span className="list-badge list-badge--off">❌ {tr('server.power_none', 'Sin alimentación')}</span>}
      </div>
    );
  }

  /** Tarjeta de servidor (vista Fichas y columnas del Kanban). */
  function renderServerCard(server, rowActions, compact = false) {
    const status = (server.status || '').toLowerCase();
    const meta = envMeta(server.environment);
    return (
      <div
        key={server.id}
        id={`server-card-${server.id}`}
        className="list-card"
        role="button"
        tabIndex={0}
        onClick={() => handleOpen(server)}
        onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(server); }}
        title={`${tr('action.view', 'Ver')} ${server.name}`}
        style={server.id === hlId ? { borderColor: 'var(--amber-600)' } : undefined}
      >
        <div className="list-card-head">
          <div>
            <div className="list-card-title">{server.name}</div>
            {server.hostname && server.hostname !== server.name && (
              <div className="list-hint">{server.hostname}</div>
            )}
          </div>
          <span className={`list-badge ${STATUS_BADGE[status] || 'list-badge--off'}`}>
            {STATUS_LABEL[status] || server.status}
          </span>
        </div>

        <div className="list-card-meta">
          {server.ip_address && <span className="list-code">{server.ip_address}</span>}
          {server.os && <span>{server.os}</span>}
        </div>

        <div className="list-card-meta">
          <span>{meta.icon} {meta.label}</span>
          {(server.role_display || server.role) && <span>{server.role_display || server.role}</span>}
        </div>

        {!compact && (server.location_name || server.rack_name) && (
          <div className="list-hint">
            📍 {[pathFor(server.location_id) || server.location_name, server.rack_name].filter(Boolean).join(' › ')}
          </div>
        )}

        {!compact && (server.cpu || server.ram_gb) && (
          <div className="list-hint">
            🔧 {[server.cpu, server.ram_gb && `${server.ram_gb} GB`, server.storage_tb && `${server.storage_tb} TB`].filter(Boolean).join(' · ')}
          </div>
        )}

        {!compact && server.owner && <div className="list-hint">👤 {server.owner}</div>}

        {renderServerFlags(server)}

        <div className="list-card-actions">{rowActions(server)}</div>
      </div>
    );
  }

  function renderServerCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((server) => renderServerCard(server, rowActions))}
      </div>
    );
  }

  /**
   * Kanban por entorno: una columna por entorno presente en la lista filtrada
   * (+ una columna para los que no tienen entorno). Es una vista de inventario,
   * no de flujo: no se arrastran tarjetas entre columnas.
   */
  function renderServerKanban({ rows, rowActions }) {
    const columns = ENV_ORDER
      .map((env) => ({ env, items: rows.filter((s) => (s.environment || '') === env) }))
      .filter((col) => col.items.length > 0 || ENV_META[col.env]);

    // Entornos que no están en el catálogo (datos antiguos): al final.
    const extras = [...new Set(rows.map((s) => s.environment || ''))]
      .filter((env) => !ENV_ORDER.includes(env));
    extras.forEach((env) => columns.push({ env, items: rows.filter((s) => (s.environment || '') === env) }));

    return (
      <div className="list-kanban">
        {columns.map((col) => {
          const meta = envMeta(col.env);
          return (
            <div key={col.env || 'sin-entorno'} className="list-kanban-col">
              <div className="list-kanban-head">
                <span>{meta.icon} {meta.label}</span>
                <span className="list-badge list-badge--on">{col.items.length}</span>
              </div>
              <div className="list-kanban-body">
                {col.items.map((server) => renderServerCard(server, rowActions, true))}
                {col.items.length === 0 && (
                  <div className="list-hint">{tr('server.kanban_empty', 'Sin servidores')}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <ServerForm
          server={selectedServer}
          companyId={companyId}
          initialMode={formMode}
          variant={estiloFicha}
          onClose={handleFormClose}
          onSave={handleFormSave}
          onClone={() => { setDialogError(''); setServerToCopy(selectedServer); }}
          onOpenServer={handleOpenServer}
          // Orden CONGELADO al abrir la ficha: guardar no reordena la lista y
          // el «siguiente» siempre es el siguiente de verdad.
          navItems={navServers}
          navIndex={selectedServer
            ? navServers.findIndex(s => String(s.id) === String(selectedServer.id))
            : -1}
          // No se toca el modo: si se venía editando, el siguiente abre en edición.
          onNavigate={(destino) => setSelectedServer(destino)}
        />
      )}

      {componentsServer && (
        <ComponentsModal
          element={componentsServer}
          companyId={companyId}
          onClose={() => setComponentsServer(null)}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={ServerStackIcon}
          title={tr('server.title', 'Servidores')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('server.new', 'Nuevo servidor')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('server.search_placeholder', 'Buscar por nombre, hostname, IP, rol…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="servidores"
          exportSheetName="Servidores"

          columns={allColumns}
          columnStorageKey="cmdb_cols_servers"
          companyId={companyId}
          importEntity="servers"
          onImported={() => fetchServers()}
          defaultVisible={defaultVisible}
          rows={filteredServers}
          rowKey={(s) => s.id}
          rowId={(s) => `server-row-${s.id}`}
          onRowClick={handleOpen}
          rowClassName={(s) => (s.id === hlId ? 'list-row-highlight' : '')}
          rowTitle={(s) => `${tr('action.view', 'Ver')} ${s.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="elements"
                supportsRack
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchServers();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="servers"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchServers();
                }}
              />
            </>
          )}

          views={SERVER_VIEWS}
          listKey="servers"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando servidores…')}
          error={error}

          empty={{
            icon: '🖧',
            title: servers.length === 0
              ? tr('server.empty_title', 'No hay servidores todavía')
              : tr('msg.no_results', 'Ningún servidor coincide con los filtros'),
            subtitle: servers.length === 0 ? tr('server.empty_hint', 'Crea el primero para empezar a inventariar.') : undefined,
            action: servers.length === 0 && companyId ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('server.new', 'Nuevo servidor')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (server) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => server.rack_id && navigate(`/infrastructure/rack/${server.rack_id}?destacar=${server.id}`)}
                  title={tr('server.view_rack_highlight', 'Ver rack con el servidor resaltado')}
                  disabled={!server.rack_id}
                >
                  <CubeIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => setComponentsServer(server)}
                  title={tr('server.components', 'Componentes (NICs / HBAs)')}
                >
                  <CpuChipIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openDisks(server)}
                  title={tr('server.disks', 'Discos y almacenamiento')}
                >
                  <CircleStackIcon />
                </button>
                {server.chassis_id != null && (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => navigate(`/infrastructure/chassis?destacar=${server.chassis_id}&abrir=1`)}
                    title={tr('server.open_chassis', 'Abrir la ficha del chasis')}
                  >
                    <ServerStackIcon />
                  </button>
                )}
                {server.agent_id && (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => { setDialogError(''); setSyncServer(server); }}
                    title={tr('server.sync_agent', 'Sincronizar con el agente (rescan)')}
                  >
                    <ArrowPathIcon />
                  </button>
                )}
              </>
            ),
            onCopy: (server) => { setDialogError(''); setServerToCopy(server); },
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('server.delete_title', 'Eliminar servidor'),
            deleteMessage: (server) => (
              <>
                {tr('server.delete_confirm', '¿Seguro que quieres eliminar el servidor')} <strong>{server.name}</strong>?{' '}
                {tr('server.delete_hint', 'Se eliminarán también sus componentes, discos y conexiones de red y alimentación. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Diálogo: copiar servidor */}
      {serverToCopy && (
        <CloneDialog
          title={tr('server.copy_title', 'Copiar servidor')}
          nameLabel={tr('server.copy_name', 'Nombre del nuevo servidor')}
          placeholder={tr('server.copy_placeholder', 'Nombre del servidor…')}
          defaultName={`${serverToCopy.name} (copia)`}
          hint={tr('server.copy_hint', 'Se copiarán el hardware, los roles, los discos (sin número de serie) y los componentes con sus puertos. No se copiarán el número de serie, la etiqueta de activo, la IP, las MAC/WWN ni la posición en el rack: el servidor nuevo nace fuera de rack.')}
          busy={Boolean(cloningId)}
          error={dialogError}
          onClose={() => { setServerToCopy(null); setDialogError(''); }}
          onConfirm={handleCopy}
        />
      )}

      {/* Diálogo: discos */}
      {disksServer && (
        <Dialog
          title={`${tr('server.disks_of', 'Discos de')} ${disksServer.name}`}
          icon="💾"
          maxWidth={640}
          onClose={() => { setDisksServer(null); setDisks(null); setDialogError(''); }}
          footer={(
            <button
              type="button"
              className="list-btn-primary"
              onClick={() => { setDisksServer(null); setDisks(null); }}
            >
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {dialogError && <div className="alert alert-danger py-2" role="alert">{dialogError}</div>}
          {!disks && !dialogError && <p className="list-hint">{tr('msg.loading', 'Cargando…')}</p>}
          {disks && (
            disks.length === 0
              ? <p>{tr('server.no_disks', 'No hay discos inventariados en este servidor.')}</p>
              : (
                <table className="list-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>{tr('server.disk_slot', 'Bahía')}</th>
                      <th>{tr('server.disk_model', 'Fabricante / Modelo')}</th>
                      <th>{tr('server.disk_capacity', 'Capacidad')}</th>
                      <th>{tr('server.disk_type', 'Tipo')}</th>
                      <th>{tr('server.disk_serial', 'Nº de serie')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disks.map((disk, idx) => (
                      <tr key={`${disk.slot || idx}`}>
                        <td><span className="list-code">{disk.slot || '—'}</span></td>
                        <td>{[disk.manufacturer, disk.model].filter(Boolean).join(' ') || '—'}</td>
                        <td>{disk.capacity_gb ? `${disk.capacity_gb} GB` : '—'}</td>
                        <td>{[disk.type, disk.interface].filter(Boolean).join(' · ') || '—'}</td>
                        <td>{disk.serial || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}
        </Dialog>
      )}

      {/* Diálogo: sincronizar con el agente */}
      {syncServer && (
        <Dialog
          title={tr('server.sync_title', 'Sincronizar con el agente')}
          icon="🔄"
          busy={busy}
          onClose={() => { setSyncServer(null); setDialogError(''); }}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => { setSyncServer(null); setDialogError(''); }}
                disabled={busy}
              >
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button type="button" className="list-btn-primary" onClick={handleSyncAgent} disabled={busy}>
                {busy ? tr('server.syncing', 'Enviando…') : tr('server.sync_now', 'Sincronizar ahora')}
              </button>
            </>
          )}
        >
          <p>
            {tr('server.sync_text', 'Se enviará al agente un ciclo de inventario (rescan) de')}{' '}
            <strong>{syncServer.name}</strong>.{' '}
            {tr('server.sync_hint', 'El agente tarda un rato en recoger el comando (consulta cada ~60 s); el resultado se verá en la ficha del servidor.')}
          </p>
          {dialogError && <div className="alert alert-danger py-2 mb-0" role="alert">{dialogError}</div>}
        </Dialog>
      )}
    </>
  );
}

export default ServersList;
